import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface ContentItem {
  id: string
  title: string
  type: string
  views: number
  unique_visitors: number
  avg_time: number
  bounce_rate: number
  shares: number
  leads: number
  conversion_rate: number
}

interface ContentByType {
  id: string
  type: string
  count: number
  total_views: number
  avg_engagement: number
  leads_generated: number
  conversion_rate: number
}

interface Statistics {
  total_content: number
  total_views: number
  total_leads: number
  avg_conversion_rate: number
  top_performing_type: string
  avg_engagement_score: number
}

export default function ContentPerformancePage() {
  const [content, setContent] = useState<ContentItem[]>([])
  const [types, setTypes] = useState<ContentByType[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [contentRes, typesRes, statsRes] = await Promise.all([
        fetch('/api/contentperformance/content'),
        fetch('/api/contentperformance/types'),
        fetch('/api/contentperformance/statistics')
      ])
      if (contentRes.ok) setContent(await contentRes.json())
      if (typesRes.ok) setTypes(await typesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching content performance data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Content Performance</h1>
          <p className="mt-2 text-gray-600">Phase 244 - Content analytics and engagement</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Content</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_content}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Views</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.total_views)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold text-green-600">{formatNumber(statistics.total_leads)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Conversion</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_conversion_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Type</p>
              <p className="text-xl font-bold text-orange-600">{statistics.top_performing_type}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Engagement Score</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.avg_engagement_score}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Content Items</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bounce</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CVR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {content.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.title}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{item.type}</span>
                    </td>
                    <td className="px-6 py-4 text-blue-600">{formatNumber(item.views)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatTime(item.avg_time)}</td>
                    <td className="px-6 py-4 text-red-600">{item.bounce_rate}%</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{formatNumber(item.leads)}</td>
                    <td className="px-6 py-4 text-purple-600 font-bold">{item.conversion_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Performance by Content Type</h2>
          </div>
          <div className="p-6 space-y-4">
            {types.map((type) => (
              <div key={type.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{type.type}</span>
                  <span className="text-lg font-bold text-green-600">{type.conversion_rate}% CVR</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Count</p>
                    <p className="font-medium">{type.count}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Views</p>
                    <p className="font-medium text-blue-600">{formatNumber(type.total_views)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Engagement</p>
                    <p className="font-medium text-purple-600">{type.avg_engagement}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Leads</p>
                    <p className="font-medium text-green-600">{formatNumber(type.leads_generated)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
